import { AudioItem } from './audio-source';
import { DatabaseModel } from './database-model';
import { MediaFileType, MediaRef, MediaFile, MediaType, MediaTypeDeprecated } from './media-file';
import { FeaturedTourMediaVariant } from './motes-paths';

/** @description Media file model returned by motes media info REST API */
export class MediaFileMeta {
    path: string = "";       // Server storage path
    size: number = 0;        // Size in bytes
    exists: boolean = false; // Describes if mote exist on server
    contentType: string;     // Http content type of file
    createdAt: string;       // String with creation date and time
    mediaLink: string;       // Public media file url
    mediaType: MediaFileType;
}

/** @description Media object info model returned by motes media info REST API */
export class MediaFileMetaList {
    totalBytes: number = 0;
    totalFiles: number = 0;
    nonExisting: number = 0;
    mediaObjects: number = 0;
    imageSize: number = 0;
    imageFiles: number = 0;
    thumbSize: number = 0;
    squareSize: number = 0;
    previewSize: number = 0;
    hdSize: number = 0;
    originSize: number = 0;
    soundSize: number = 0;
    soundFiles: number = 0;
    videoSize: number = 0;
    videoFiles: number = 0;
    mediaFileList: MediaFileMeta[] = [];
}

/** @description Enum that describes in what context the media is used */
export enum ContainerType {
    MOTE = 'MOTE',
    WORLD = 'WORLD',
    PRODUCT = 'PRODUCT',
    PROFILE = 'PROFILE',
    FEATURED_TOURS = 'FEATURED_TOURS',
    BLOG_POST = 'BLOG_POST',
    MEDIA_LIB = 'MEDIA_LIB',
}

export type ContainerOptions =
    {
        type: ContainerType.PROFILE;
        containerKey: string;
    } | {
        type: ContainerType.MOTE;
        containerKey: string;
        mediaKey: string;
    } | {
        type: ContainerType.PRODUCT;
        containerKey: string;
        mediaIdx: number;
    } | {
        type: ContainerType.FEATURED_TOURS;
        containerKey: string;
        tourVariant: FeaturedTourMediaVariant;
    } | {
        type: ContainerType.WORLD;
        containerKey: string;
    } | {
        type: ContainerType.BLOG_POST;
        containerKey: string;
    } | {
        type: ContainerType.MEDIA_LIB;
        mediaKey: string;
    };

/** @description The Media class is used for all types of Media. Reference paths are available from MediaFileType Enum. */
export class Media extends DatabaseModel implements MediaRef {
    public static readonly MEDIA_UPLOAD_PATH = "media/";

    public createdOn: number | any = 0;
    public createdBy: string = "";
    public project: string = "";

    // Type
    public type: MediaType = MediaType.UNDEFINED;
    public key: string = "";
    public files?: MediaFile[] = [];

    // Media order (starting from 0. -1 denotes unordered) used by mote to save mote specific order (not saved with toJson() of media object)
    public order?: number = -1;

    // Text
    public title: string = "";
    public description: string = "";
    public copyright: string = "";

    // Files contained in media object
    public original = new MediaFile(MediaFileType.ORIGINAL, MediaType.IMAGE, this);
    // public cropped = new MediaFile(MediaFileType.CROPPED, MediaType.IMAGE, this);
    /** @asType string */
    public hd?: MediaFile = new MediaFile(MediaFileType.HD, MediaType.IMAGE, this);
    /** @asType string */
    public preview?: MediaFile = new MediaFile(MediaFileType.PREVIEW, MediaType.IMAGE, this);
    /** @asType string */
    public square?: MediaFile = new MediaFile(MediaFileType.SQUARE, MediaType.IMAGE, this);
    /** @asType string */
    public thumb?: MediaFile = new MediaFile(MediaFileType.THUMB, MediaType.IMAGE, this);
    /** @asType string */
    public sound?: MediaFile = new MediaFile(MediaFileType.SOUND, MediaType.SOUND, this);
    /** @asType string */
    public ambisonic?: MediaFile = new MediaFile(MediaFileType.AMBISONIC, MediaType.AMBISONIC, this);
    /** @asType string */
    public video?: MediaFile = new MediaFile(MediaFileType.VIDEO, MediaType.VIDEO, this);

    // Additional medial sources (for now dynamically loaded for passing through media player service)
    public augmentedAudioList?: AudioItem[] = [];

    // Extras
    public mime?: string = "";
    public meta?: any = "";
    public htmlPlayer?: any;
    public uploading?: number = 0;

    // Indicates if the content of this media was auto generated (image or video was automatically generated by the system)
    public isAutoGenerated?: boolean = false;
    // Saved
    public isSaved?: boolean = true;
    // Indicate a user request for deletion of media object
    public deleteMedia?: boolean = false;
    // Indicates if the image contains text (used for filtering in TTD export)
    public imageHasText?: boolean = false;

    /**
     * Create a Media object containing media urls (origin, thumb, square, sound, video etc.)
     * @param json Media json object containing title, description, mime, key and prop urls etc.
     * @param key Media key (always overwrites json key if set)
     * @param placeholder Optional url of placeholder image used if prop url is empty
     * @param group Optional group string user defined context (this is added to url in order to identify the type/group of media requests. Ex. 'image', 'icon' etc.)
     */
    constructor(json: any = null, key: string = "", public placeholder?: string, public group?: string) {
        super();

        if(json) {
            this.fromJson(json, placeholder);
        }
        this.key = key || json?.key || DatabaseModel.genKey();
    }

    public fromJson(data: any, placeholder = ""): Media {
        if(!data) return null;

        this.createdOn = data.createdOn || 0;
        this.createdBy = data.createdBy || "";
        this.project = data.project || "";

        this.order = data.order >= 0 ? data.order : -1;

        this.title = data.title || "";
        this.description = data.description || "";
        this.copyright = data.copyright || "";
        // Convert deprecated type number to string
        // TODO: Delete when all media has been converted
        this.type = MediaType[data.type] ? data.type : this.deprecatedMediaTypeToNew(data.type);

        this.mime = data.mime || "";
        this.meta = data.meta || {};
        this.augmentedAudioList = data.augmentedAudioList || [];
        this.isAutoGenerated = data.isAutoGenerated || false;
        this.imageHasText = data.imageHasText || false;

        this.key = data.key || "";
        if(this.key.startsWith("legacy")) this.key = "";

        // Go through media property reference and populate media based on this
        for(let file of this.files) {
            file.fromJson(data, placeholder);
            // Set specific media video type on sound files (MediaType.AMBISONIC) - the rest of the media types are initialized by file constructor
            if(file.type == MediaFileType.SOUND && (this.type == MediaType.AMBISONIC)) {
                file.mediaType = this.type;
            }

            // Set specific media video type on video files (MediaType.FILMCENTRALEN_VIDEO, MediaType.YOUTUBE_VIDEO or MediaType.VIDEO)
            if(file.type == MediaFileType.VIDEO && (this.type == MediaType.YOUTUBE_VIDEO || this.type == MediaType.FILMCENTRALEN_VIDEO)) {
                file.mediaType = this.type;
            }
        }

        return this;
    }

    private deprecatedMediaTypeToNew(type: MediaTypeDeprecated): string {
        switch(type) {
            case MediaTypeDeprecated.AMBISONIC: return MediaType.AMBISONIC;
            case MediaTypeDeprecated.IMAGE: return MediaType.IMAGE;
            case MediaTypeDeprecated.SOUND: return MediaType.SOUND;
            case MediaTypeDeprecated.VIDEO: return MediaType.VIDEO;
            default:
            case MediaTypeDeprecated.UNDEFINED: return MediaType.UNDEFINED;
        }
    }

    public copy(key: string = this.key): Media {
        let json = this.toJson();
        return new Media(json, key, this.placeholder);
    }

    public toJson(): any {
        let json: any = {};

        // Go through media property reference and populate json
        for(let file of this.files) {
            if(!file.url) continue;
            Object.assign(json, file.toJson());
        }

        json.createdOn = this.createdOn || Date.now();
        json.createdBy = this.createdBy || '';
        json.project = this.project;

        // Add remaining props to json
        json.title = this.title;
        json.description = this.description;
        json.type = this.type;
        // Fix previous invalid saved sound media objects
        if(this.type == MediaType.IMAGE && this['sound'].url) json.type = MediaType.SOUND;

        json.copyright = this.copyright;
        json.key = this.key;
        json.augmentedAudioList = this.augmentedAudioList;
        json.isAutoGenerated = this.isAutoGenerated;
        json.imageHasText = this.imageHasText;
        json.order = this.order;

        // Return some good old json
        return json;
    }

    public toText(): string {
        let str = '';
        for(let key in MediaFileType) {
            let prop = MediaFileType[key];
            str += this[prop] + ', ';
        }
        return str.replace(/.$/, ".");
    }

    /**
     * Get files of media object (only non empty paths)
     */
    public getFiles(): MediaFile[] {
        const primaryFiles = this.files.filter(file => file.url);
        const fallbackFiles = this.files.filter(file => file.fallback);
        return primaryFiles.concat(fallbackFiles);
    }

    /**
    * Get storage paths of media object (only non empty paths)
    */
    public getStoragePaths(): string[] {
        return this.files.filter(file => file.storage).map(file => file.storage);
    }

    /**
     * Set the storage path of files in this media object
     * Std path is: `media/${project}/${media.key}_${media.file.fileType}`
     * ie. base path should be `media/${project}` for motes media
     * This should be called only once after unique media key have been generated and set
     */
    public initStoragePaths(basePath: string, overwritePath: boolean = false) {
        if(!this.key) return console.warn("No key in media, can't init storage path");

        for(let file of this.files) {
            // TODO: Consider adding mime information to each file and add extension here
            // however this would require some tweaking because of legacy data format in DB
            // Don't change already initialized storage paths, unless  file.url has been deleted
            const isAutogenerated = file.mediaType == MediaType.IMAGE && file.type != MediaFileType.ORIGINAL;
            if(!isAutogenerated && (!file.storage || (file.storage && !file.url) || overwritePath)) {
                file.storage = `${basePath}/${this.key}_${file.type}`;
            }
        }
    }

    public static mediaTypeToString(type: MediaType): string {
        return MediaType[type].toLowerCase();
    }
    /** Map of icons generally used for media files */
    public static readonly FileIcons: { [key in MediaType]?: string } = {
        IMAGE: 'image',
        VIDEO: 'video',
        YOUTUBE_VIDEO: 'video',
        FILMCENTRALEN_VIDEO: 'video',
        SOUND: 'headphones',
        AMBISONIC: 'headphones'
    };


}
